import { Link } from 'react-router-dom'
import { Modal, Button, Badge } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { usePermissions } from '@/hooks/usePermissions'
import { useApprovePenaltyEntry, useReversePenaltyEntry } from '@/features/penalties/hooks/usePenalties'
import { formatCurrency, formatDate, formatDateTime } from '@/lib/formatters'
import {
  penaltyStatusBadgeVariant,
  penaltyStatusLabel,
  resolveEmployeeName,
  resolvePenaltyTypeName,
} from './penaltyUtils'
import type { PenaltyEntry, PenaltyType } from '@/features/penalties/types/penalty.types'

interface PenaltyDetailModalProps {
  companyId: string
  entry: PenaltyEntry | null
  types: PenaltyType[]
  onClose: () => void
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <span className="shrink-0 font-medium text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-right text-gray-900 dark:text-gray-100">{value}</span>
    </div>
  )
}

export function PenaltyDetailModal({
  companyId,
  entry,
  types,
  onClose,
}: PenaltyDetailModalProps): React.ReactElement | null {
  const { can } = usePermissions()
  const approveMutation = useApprovePenaltyEntry()
  const reverseMutation = useReversePenaltyEntry()

  if (!entry) return null

  const canApprove = can('penalties', 'approve')
  const canReverse = can('penalties', 'reverse')
  const canReadAudit = can('audit', 'read')

  const isPending = approveMutation.isPending || reverseMutation.isPending

  const handleApprove = () => {
    approveMutation.mutate(
      { companyId, entryId: entry._id },
      { onSuccess: onClose },
    )
  }

  const handleReverse = () => {
    reverseMutation.mutate(
      { companyId, entryId: entry._id },
      { onSuccess: onClose },
    )
  }

  const typeName = resolvePenaltyTypeName(
    entry.penaltyTypeId,
    entry.customTypeName,
    types,
    entry.penaltyTypeName,
  )
  const employeeName = resolveEmployeeName(entry.employeeName, entry.employeeId, entry.userId)
  const formattedAmount = formatCurrency(entry.amount, entry.currency)

  const showApprove = canApprove && entry.status === 'pending_approval'
  const showReverse = canReverse && entry.status !== 'reversed'

  return (
    <Modal
      open={!!entry}
      onClose={onClose}
      title="Penalty details"
      footer={
        showApprove || showReverse ? (
          <div className="flex flex-wrap gap-2">
            {showApprove && (
              <Button
                type="button"
                variant="primary"
                onClick={handleApprove}
                loading={isPending}
              >
                {approveMutation.isPending ? 'Approving…' : 'Approve'}
              </Button>
            )}
            {showReverse && (
              <Button
                type="button"
                variant="danger"
                onClick={handleReverse}
                loading={isPending}
              >
                {reverseMutation.isPending ? 'Reversing…' : 'Reverse'}
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
              Close
            </Button>
          </div>
        ) : (
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {isPending ? (
        <SectionLoader variant="detail" label="Loading penalty" />
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          <DetailRow
            label="Status"
            value={
              <Badge variant={penaltyStatusBadgeVariant(entry.status)}>
                {penaltyStatusLabel(entry.status)}
              </Badge>
            }
          />
          <DetailRow label="Type" value={typeName} />
          <DetailRow label="Employee" value={employeeName} />
          <DetailRow label="Amount" value={formattedAmount} />
          <DetailRow label="Date" value={formatDate(entry.penaltyDate)} />
          {entry.reason && <DetailRow label="Reason" value={entry.reason} />}
          <DetailRow
            label="Issued by"
            value={<span className="font-mono text-xs">{entry.createdBy}</span>}
          />
          <DetailRow label="Issued at" value={formatDateTime(entry.createdAt)} />
          {entry.approvedBy && (
            <DetailRow
              label="Approved by"
              value={<span className="font-mono text-xs">{entry.approvedBy}</span>}
            />
          )}
          {entry.approvedAt && (
            <DetailRow label="Approved at" value={formatDateTime(entry.approvedAt)} />
          )}
          {canReadAudit && (
            <div className="pt-2">
              <Link
                to={`/audit-logs?module=penalties&entityId=${entry._id}&entityType=penalty_entry`}
                className="text-sm text-primary underline underline-offset-2 hover:opacity-80"
                onClick={onClose}
              >
                View audit history
              </Link>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

PenaltyDetailModal.displayName = 'PenaltyDetailModal'
